import React from 'react';
import ActionMenu, { type ActionMenuItem } from '../ActionMenu';
import Alert, { type AlertType } from '../Alert';
import Breadcrumbs from '../Breadcrumbs';
import { hasBackButton } from '../breadcrumbsNav';
import Button from '../Button';
import OverflowRow from '../OverflowRow';
import Skeleton from '../Skeleton';
import PageTitle from '../Typography/PageTitle';
import Text from '../Typography/Text';
import { safeHref } from '../safeHref';
import {
    CARD_CLASSES,
    CARD_MIN_HEIGHT,
    CARD_RADIUS,
    CONTENT_INSET_VAR,
    CONTENT_MIN_HEIGHT,
    DIVIDER_CLASSES,
    HEADER_HEIGHT,
} from './constants';
import type { BreadcrumbLink, PageAction, RenderBreadcrumbLink } from './types';

export type PageContentGap = 'small' | 'medium' | 'large' | 'xlarge';

export interface RegularLayoutProps {
    breadcrumbLinks?: BreadcrumbLink[];
    breadcrumbActions?: PageAction[];
    renderLink?: RenderBreadcrumbLink;
    title?: React.ReactNode;
    titleSuffix?: React.ReactNode;
    subtitle?: string;
    description?: React.ReactNode;
    buttons?: PageAction[];
    /**
     * Hides the buttons and skeletons the title. `subtitle` and `description` are only
     * skeletoned when those props are also passed.
     */
    loading?: boolean;
    children: React.ReactNode;
    centered?: boolean;
    noPadding?: boolean;
    contentGap?: PageContentGap;
    alert?: { message: string; type?: AlertType };
    /** Demotes the default `main` landmark — pass e.g. `region` when the app owns its own. */
    role?: React.AriaRole;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    style?: React.CSSProperties;
}

const CONTENT_GAPS: Record<PageContentGap, number> = {
    small: 8,
    medium: 16,
    large: 24,
    xlarge: 40,
};

const isDanger = (button: PageAction): boolean =>
    button.variant === 'destructive' || button.variant === 'error';

// ActionMenuItem has no link form, so a collapsed link action opens the tab itself.
const menuAction = (button: PageAction): (() => void) | undefined => {
    const href = safeHref(button.href);
    if (!href) {
        return button.onClick;
    }
    return () => window.open(href, button.target ?? '_blank', 'noopener,noreferrer');
};

const toMenuItems = (buttons: PageAction[]): ActionMenuItem[] =>
    buttons.flatMap((button, index) => {
        const item: ActionMenuItem = {
            key: `${index}-${button.label}`,
            label: button.label,
            onClick: menuAction(button),
            disabled: button.disabled,
            icon: button.icon,
            tooltip: button.tooltip,
            danger: isDanger(button),
        };
        return button.dividerBefore && index > 0
            ? [{ type: 'divider' as const, key: `divider-${index}-${button.label}` }, item]
            : [item];
    });

// Not `ActionBar`: it measures by walking ancestors for `clientWidth`, and this chrome
// mounts on every page. OverflowRow batches its measurement instead.
// A divider must live in the same OverflowRow child as its button — a standalone child
// would survive its hidden button and break the child-index-to-button mapping.
/**
 * The page's action buttons, collapsing into an overflow menu as the row narrows. Exported so
 * a page that renders its own header block — e.g. one that scrolls the title away — keeps the
 * same behaviour instead of hand-rolling `buttons.map(...)` and losing the collapse, the
 * dividers and the href handling.
 */
export const PageActions: React.FC<{ buttons: PageAction[] }> = ({ buttons }) => {
    if (buttons.length === 0) {
        return null;
    }

    // min-w-0 gives OverflowRow a shrinkable container to collapse against.
    return (
        <div className="min-w-0">
            <OverflowRow
                gap={8}
                justify="end"
                renderOverflow={(hiddenCount) => (
                    <ActionMenu
                        items={toMenuItems(
                            buttons.slice(Math.max(0, buttons.length - hiddenCount)),
                        )}
                    />
                )}
            >
                {buttons.map((button, index) => {
                    const href = safeHref(button.href);
                    return (
                        <div key={`${index}-${button.label}`} className="flex items-center gap-2">
                            {button.dividerBefore && index > 0 ? (
                                <span
                                    aria-hidden="true"
                                    className="h-4 w-px bg-[var(--malachite-card-border)]"
                                />
                            ) : null}
                            <Button
                                variant={button.variant ?? 'filled'}
                                size="medium"
                                disabled={button.disabled}
                                icon={button.icon}
                                tooltip={button.tooltip}
                                tooltipPlacement={button.tooltipPlacement}
                                href={href}
                                target={href ? (button.target ?? '_blank') : undefined}
                                onClick={href ? undefined : button.onClick}
                            >
                                {button.label}
                            </Button>
                        </div>
                    );
                })}
            </OverflowRow>
        </div>
    );
};

const NO_LINKS: BreadcrumbLink[] = [];
const NO_BUTTONS: PageAction[] = [];

const RegularLayout: React.FC<RegularLayoutProps> = ({
    breadcrumbLinks = NO_LINKS,
    breadcrumbActions,
    renderLink,
    title,
    titleSuffix,
    subtitle,
    description,
    buttons = NO_BUTTONS,
    loading = false,
    children,
    centered = false,
    noPadding = false,
    contentGap = 'small',
    alert,
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    style,
}) => {
    const hasBack = hasBackButton(breadcrumbLinks);
    const contentPadding = noPadding ? 0 : centered ? '5vh 14vw' : '40px';
    // Published so a full-bleed child (CardStrip's bottom rule) can cancel it and re-apply
    // it as its own padding, instead of hardcoding the gutter at the call site.
    const contentInset = noPadding ? '0px' : centered ? '14vw' : '40px';
    const hasHeaderBlock = Boolean(
        loading || title || subtitle || description || buttons.length > 0,
    );

    const subtitleNode = subtitle ? (
        loading ? (
            <Skeleton.Input width={120} height={24} />
        ) : (
            <Text color="secondary" size="small">
                {subtitle}
            </Text>
        )
    ) : null;

    const titleNode = loading ? (
        <Skeleton.Input width={200} height={40} />
    ) : title ? (
        <div className="flex items-center" style={{ gap: 12 }}>
            <PageTitle>{title}</PageTitle>
            {titleSuffix}
        </div>
    ) : null;

    const descriptionNode = description ? (
        loading ? (
            <Skeleton.Input width={120} height={24} />
        ) : (
            <Text color="secondary" style={{ lineHeight: 1.6 }}>
                {description}
            </Text>
        )
    ) : null;

    return (
        <main
            className={`${CARD_CLASSES} ${className}`}
            style={{ borderRadius: CARD_RADIUS, minHeight: CARD_MIN_HEIGHT, ...style }}
            role={role}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-busy={loading || undefined}
        >
            <div
                className={`flex items-center ${DIVIDER_CLASSES}`}
                style={{
                    height: HEADER_HEIGHT,
                    paddingInlineStart: hasBack ? 16 : 28,
                    paddingInlineEnd: 16,
                }}
            >
                <Breadcrumbs
                    links={breadcrumbLinks}
                    actions={breadcrumbActions}
                    renderLink={renderLink}
                    className="flex-1 min-w-0"
                />
            </div>

            {alert ? (
                <div className={DIVIDER_CLASSES} style={{ padding: '10px 16px' }}>
                    <Alert message={alert.message} type={alert.type} hasMargin={false} />
                </div>
            ) : null}

            <div
                className="flex flex-col"
                style={
                    {
                        padding: contentPadding,
                        minHeight: CONTENT_MIN_HEIGHT,
                        gap: CONTENT_GAPS[contentGap],
                        [CONTENT_INSET_VAR]: contentInset,
                    } as React.CSSProperties
                }
            >
                {hasHeaderBlock ? (
                    <div className="flex flex-col" style={{ gap: 12 }}>
                        {titleNode || subtitleNode || buttons.length > 0 ? (
                            <div
                                className="flex flex-wrap items-center justify-between"
                                style={{ gap: 12 }}
                            >
                                <div
                                    className="flex flex-col flex-auto min-w-0 overflow-hidden"
                                    style={{ gap: 4 }}
                                >
                                    {subtitleNode}
                                    {titleNode}
                                </div>
                                {loading ? null : <PageActions buttons={buttons} />}
                            </div>
                        ) : null}
                        {descriptionNode}
                    </div>
                ) : null}
                {children}
            </div>
        </main>
    );
};

export default RegularLayout;
export { RegularLayout };
